"use client"

import { useState, useEffect, useCallback, useMemo, JSX } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { useAutoRefresh } from "@/hooks/useAutoRefresh"
import { listWorkflowJobs } from "@/services/hpcJobsService"
import { HpcJob } from "@/types/hpc-jobs"
import { transformJobData, getStatusVariant, isRunningStatus, formatCostUsd } from "@/lib/hpc-jobs-utils"
import { WorkflowDAG } from "@/components/hpc-jobs/WorkflowDAG"
import { CancelJobButton } from "@/components/hpc-jobs/CancelJobButton"
import { ArrayJobStalenessInfo } from "@/components/hpc-jobs/ArrayJobStalenessInfo"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { CopyButton } from "@/components/ui/copy-button.tsx"
import { ArrowLeft, FileText, Loader2, RefreshCw } from "lucide-react"
import { Link } from "react-router-dom"
import { JobStatus } from "@/types/hpc-jobs"

/** Auto-refresh interval in seconds. */
const AUTO_REFRESH_INTERVAL = 15

/**
 * Formats a Date for display in the jobs table.
 * Uses Australia/Perth timezone to match the HPC team's locale.
 *
 * @param date - The date to format, or undefined.
 * @returns A formatted string like "14:30 28-05-2026", or "-" if invalid.
 */
function formatTimestamp(date: Date | undefined): string {
  if (!date || isNaN(date.getTime()) || date.getTime() === 0) {
    return "-"
  }

  return new Intl.DateTimeFormat("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Australia/Perth",
    hour12: false,
  }).format(date)
}

/**
 * Formats array status breakdown into a tooltip string.
 *
 * @param arrayStatuses - Status counts for an array job.
 * @returns A comma-separated summary, or undefined.
 */
function formatArrayStatusTooltip(arrayStatuses?: Record<JobStatus, number>): string | undefined {
  if (!arrayStatuses) return undefined
  const order: JobStatus[] = [
    JobStatus.SUCCEEDED,
    JobStatus.FAILED,
    JobStatus.RUNNING,
    JobStatus.STARTING,
    JobStatus.RUNNABLE,
    JobStatus.PENDING,
    JobStatus.SUBMITTED,
  ]
  const parts: string[] = []
  for (const status of order) {
    const count = arrayStatuses[status]
    if (count && count > 0) {
      parts.push(`${status}: ${count}`)
    }
  }
  return parts.length > 0 ? parts.join(", ") : undefined
}

/**
 * HPC Workflow detail page component.
 *
 * Displays all jobs within a workflow, including:
 * - A DAG visualization showing job dependencies
 * - A table of jobs with status, timestamps, and log links
 * - Auto-refresh functionality
 *
 * Route params:
 *   - workflowId: The workflow ID (from URL path)
 */
export default function HpcWorkflowDetailPage(): JSX.Element {

  const { workflowId } = useParams<{ workflowId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user,  getAccessToken } = useAuth()

  // Data state
  const [jobs, setJobs] = useState<HpcJob[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Get the username from Auth0 user object
  const username = user?.username ?? user?.name ?? null

  /**
   * Fetches jobs for this workflow from the API.
   */
  const fetchWorkflowJobs = useCallback(async (): Promise<void> => {
    if (!workflowId) return

    setIsLoading(true)
    setError(null)

    try {
      const token = await getAccessToken()
      const response = await listWorkflowJobs({ user: username, workflowId, includePrice: true }, token)

      // Transform raw API data into typed HpcJob objects
      const transformedJobs = (response.jobs as Record<string, unknown>[]).map(transformJobData)

      // Sort by start time, most recent first
      transformedJobs.sort((a, b) => {
        const timeA = a.startedAt?.getTime() || a.submittedAt?.getTime() || 0
        const timeB = b.startedAt?.getTime() || b.submittedAt?.getTime() || 0
        return timeB - timeA
      })

      setJobs(transformedJobs)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load workflow jobs"
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [workflowId, username, getAccessToken])

  // Fetch on mount
  useEffect(() => {
    fetchWorkflowJobs()
  }, [fetchWorkflowJobs])

  const { autoRefresh, setAutoRefresh, countdown } = useAutoRefresh({
    onRefresh: fetchWorkflowJobs,
    intervalSeconds: AUTO_REFRESH_INTERVAL,
  })

  // Compute aggregate workflow status for display
  const workflowStatus = useMemo((): JobStatus => {
    if (jobs.length === 0) return JobStatus.PENDING

    const hasFailed = jobs.some((job) => job.status === JobStatus.FAILED)
    if (hasFailed) return JobStatus.FAILED

    const allSucceeded = jobs.every((job) => job.status === JobStatus.SUCCEEDED)
    if (allSucceeded) return JobStatus.SUCCEEDED

    const hasRunning = jobs.some((job) =>
      [JobStatus.RUNNING, JobStatus.STARTING].includes(job.status)
    )
    if (hasRunning) return JobStatus.RUNNING

    return JobStatus.PENDING
  }, [jobs])

  /**
   * Builds the link to an array job's child tasks page, carrying through this
   * page's return params so the user can navigate back out to the overview.
   *
   * @param arrayJobId - The parent array job ID to link to.
   * @returns A router path to the array job page with preserved return params.
   */
  function buildArrayJobHref(arrayJobId: string): string {
    const params = new URLSearchParams()

    const returnTab = searchParams.get("tab")
    const returnRange = searchParams.get("range")
    if (returnTab) params.set("tab", returnTab)
    if (returnRange) params.set("range", returnRange)
    if (searchParams.get("from") === "admin") params.set("from", "admin")

    // Mark this workflow as the origin so the array job page's Back button
    // returns here rather than to the jobs overview.
    if (workflowId) params.set("fromWorkflow", workflowId)

    const queryString = params.toString()
    const path = `/computing/hpc/array-job/${encodeURIComponent(arrayJobId)}`

    return queryString ? `${path}?${queryString}` : path
  }

  /**
   * Handles back navigation, preserving the return params if provided.
   * Navigates to the admin view if the user came from there.
   */
  const handleBack = (): void => {
    const returnTab = searchParams.get("tab")
    const returnRange = searchParams.get("range")
    const from = searchParams.get("from")

    const basePath = from === "admin" ? "/computing/hpc/admin" : "/computing/hpc"

    const params = new URLSearchParams()
    if (returnTab) params.set("tab", returnTab)
    if (returnRange) params.set("range", returnRange)
    const queryString = params.toString()
    navigate(queryString ? `${basePath}?${queryString}` : basePath)
  }

  return (
    <>
    <title>HPC Workflow - MAP Apps Platform</title>
    <div className="flex flex-1 flex-col min-h-0 overflow-y-auto p-6">
      <div className="flex flex-col gap-6 h-full">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold">Workflow</h1>
              <div className="flex items-center gap-1">
                <CopyButton text={workflowId || ""} />
                <code className="text-sm text-muted-foreground font-mono">{workflowId}</code>
              </div>
              {jobs.length > 0 && (
                <Badge variant={getStatusVariant(workflowStatus)}>{workflowStatus}</Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Auto-refresh toggle */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={e => setAutoRefresh(e.target.checked)}
                className="h-4 w-4"
              />
              Auto-refresh
              {autoRefresh && <span className="text-muted-foreground">({countdown}s)</span>}
            </label>
            {/* Manual refresh */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchWorkflowJobs()}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div className="rounded-md border border-destructive bg-destructive/10 p-4">
            <p className="text-sm text-destructive">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchWorkflowJobs()}
              className="mt-2"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Loading state */}
        {isLoading && jobs.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* DAG Visualization */}
        {(!isLoading || jobs.length > 0) && !error && (
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-medium">Workflow DAG</h2>
            <WorkflowDAG jobs={jobs} />
          </div>
        )}

        {/* Jobs Table */}
        {(!isLoading || jobs.length > 0) && !error && (
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-medium">Jobs ({jobs.length})</h2>
            {jobs.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                No jobs found in this workflow.
              </div>
            ) : (
              <div className="rounded-md border overflow-hidden">
                <Table className="table-fixed w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[22%]">Job Name</TableHead>
                      <TableHead className="w-[22%]">Job ID</TableHead>
                      <TableHead className="w-[11%]">Status</TableHead>
                      <TableHead className="w-[13%]">Start Time</TableHead>
                      <TableHead className="w-[13%]">End Time</TableHead>
                      <TableHead className="w-[7%] text-right">Cost</TableHead>
                      <TableHead className="w-[6%]">Logs</TableHead>
                      <TableHead className="w-[6%]"><span className="sr-only">Cancel</span></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobs.map(job => {
                      const statusTooltip = job.arrayStatuses
                        ? formatArrayStatusTooltip(job.arrayStatuses)
                        : job.status === JobStatus.FAILED && job.statusReason
                          ? job.statusReason
                          : undefined

                      // Only show logs button for jobs that have reached execution
                      const hasLogs = [
                        JobStatus.STARTING,
                        JobStatus.RUNNING,
                        JobStatus.SUCCEEDED,
                        JobStatus.FAILED,
                      ].includes(job.status)

                      return (
                        <TableRow key={job.jobId}>
                          <TableCell className="truncate" title={job.jobName}>
                            {job.jobName}
                          </TableCell>
                          <TableCell className="overflow-hidden">
                            <div className="flex items-center gap-1 min-w-0">
                              <CopyButton text={job.jobId} />
                              {(job.arraySize ?? 0) > 0 ? (
                                <Link
                                  to={buildArrayJobHref(job.jobId)}
                                  className="font-mono text-xs truncate hover:underline text-primary"
                                  title={`${job.jobId} — view ${job.arraySize} child tasks`}
                                >
                                  {job.jobId}
                                </Link>
                              ) : (
                                <span className="font-mono text-xs truncate" title={job.jobId}>
                                  {job.jobId}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <Badge variant={getStatusVariant(job.status)} title={statusTooltip}>
                                {job.status}
                              </Badge>
                              <ArrayJobStalenessInfo job={job} />
                            </div>
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            {formatTimestamp(job.startedAt)}
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            {formatTimestamp(job.stoppedAt)}
                          </TableCell>
                          <TableCell className="text-sm text-right whitespace-nowrap">
                            {formatCostUsd(job.totalUsd)}
                          </TableCell>
                          <TableCell>
                            {hasLogs && (
                              <Link
                                to={`/computing/hpc/logs/${encodeURIComponent(job.jobId)}?jobName=${encodeURIComponent(job.jobName)}&status=${encodeURIComponent(job.status)}${(job.arraySize ?? 0) > 0 ? `&arraySize=${job.arraySize}` : ""}`}
                              >
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  title="View logs"
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              </Link>
                            )}
                          </TableCell>
                          <TableCell>
                            {/* Only running jobs can be cancelled. */}
                            {isRunningStatus(job.status) && (
                              <CancelJobButton
                                cancelId={job.jobId}
                                name={job.jobName}
                                entityType="job"
                                onCancelled={fetchWorkflowJobs}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  )
}
